import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';

// ============================================================================
// MUSEUM-QUALITY COMPONENTS
// ============================================================================

// Philosophical Statement Component - Pentagram Style
const PhilosophicalStatement = () => {
  return (
    <div 
      className="w-full py-32 md:py-40 lg:py-48 bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/003white_transparent_cropped.png)`,
        backgroundSize: '168% auto',
      }}
    >
      <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="max-w-5xl mx-auto text-center">
          <blockquote
            className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-white leading-[1.15]"
            style={{
              fontFamily: 'Arial, Helvetica, sans-serif',
              letterSpacing: '-0.03em',
              fontWeight: 300,
            }}
          >
            MASS BODY reimagines the human body as part of Earth's living system. An ecological sculpture that transforms through time. By embracing decomposition as a shared creative act between human and environment, MASS BODY invites a profound rethinking of life and our material continuity with nature.
          </blockquote>
        </div>
      </div>
    </div>
  );
};

// Press Section - Pentagram Style
const PressSection = () => {
  return (
    <div className="w-full py-24 md:py-32 lg:py-40 border-t border-black/5">
      <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left Column - Section Title */}
          <div className="lg:col-span-5 flex justify-start lg:justify-end">
            <div className="text-left lg:text-right max-w-md">
              <h2 
                className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-black leading-[1.05]"
                style={{ 
                  fontFamily: 'Arial, Helvetica, sans-serif', 
                  letterSpacing: '-0.04em',
                  fontWeight: 300,
                }}
              >
                Press
              </h2>
            </div>
          </div>
          
          {/* Right Column - Content */}
          <div className="lg:col-span-7">
            <div className="space-y-10">
              {/* YouTube Video */}
              <div className="w-full aspect-video">
                <iframe 
                  src="https://www.youtube.com/embed/ATxLG_DoySw" 
                  title="YouTube video player" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  referrerPolicy="strict-origin-when-cross-origin" 
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
              
              {/* Office Magazine Article */}
              <div>
                <a
                  href="https://officemagazine.net/exploring-intimacy-mass-body"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group"
                >
                  <div className="flex items-start gap-6 pb-6 border-b border-black/5 group-hover:border-black/20 transition-colors">
                    <div className="flex-shrink-0">
                      <img
                        src="https://www.pngkit.com/png/full/122-1229568_logo-office-magazine.png"
                        alt="Office Magazine"
                        className="h-12 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                    <div className="flex-1">
                      <p
                        className="text-sm md:text-base text-black/60 mb-2 uppercase tracking-[0.05em]"
                        style={{
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          fontWeight: 400,
                        }}
                      >
                        Office Magazine
                      </p>
                      <h3
                        className="text-lg md:text-xl lg:text-2xl text-black leading-[1.2] group-hover:opacity-70 transition-opacity"
                        style={{
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          fontWeight: 400,
                          letterSpacing: '-0.02em',
                        }}
                      >
                        Exploring Intimacy with MASS BODY
                      </h3>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// About Section Component - Pentagram Style
const AboutSection = () => {
  const organizations = [
    { name: 'FCA', logo: 'fca-logo.jpg' },
    { name: 'USLAF', logo: 'uslaf-logo.jpg' },
    { name: 'Mellon Foundation', logo: 'mellon-logo.jpg' },
    { name: 'Ford Foundation', logo: 'ford-logo.jpg' },
    { name: 'ISCP', logo: 'iscp-logo.jpg' },
    { name: 'AWF', logo: 'AWF-logo.jpg' },
  ];

  return (
    <div className="w-full py-24 md:py-32 lg:py-40 border-t border-black/5">
      <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left Column - Section Title */}
          <div className="lg:col-span-5 flex justify-start lg:justify-end">
            <div className="text-left lg:text-right max-w-md">
              <h2 
                className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-black leading-[1.05]"
                style={{ 
                  fontFamily: 'Arial, Helvetica, sans-serif', 
                  letterSpacing: '-0.04em',
                  fontWeight: 300,
                }}
              >
                About
              </h2>
            </div>
          </div>
          
          {/* Right Column - Content */}
          <div className="lg:col-span-7">
            <div className="max-w-2xl space-y-8">
              <p
                className="text-lg md:text-xl text-black/80 leading-[1.6]"
                style={{
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  fontWeight: 400,
                }}
              >
                MASS BODY is an initiative by Angel Lartigue, whose work has been supported by the FCA, USLAF, Mellon Foundation, Ford Foundation, Andy Warhol Foundation, and ISCP.
              </p>
              
              {/* Supporting Institutions - Logo Grid */}
              <div className="flex flex-wrap items-center justify-start gap-8 md:gap-12 pt-2">
                {organizations.map((org, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-center"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/${org.logo}`}
                      alt={org.name}
                      className="h-14 md:h-16 w-auto object-contain"
                      style={{ filter: 'grayscale(100%)', opacity: 0.6 }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Primary CTA Component - Pentagram Style
const PrimaryCTA = ({ onClick, isProminent = false }) => {
  return (
    <div className={`w-full ${isProminent ? 'py-32 md:py-40 lg:py-48' : 'py-24 md:py-32 lg:py-40'} bg-white ${isProminent ? '' : 'border-t border-black/5'}`}>
      <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">
        {isProminent ? (
          // Prominent version - Pentagram asymmetric layout
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Left Column - Title */}
            <div className="lg:col-span-5 flex justify-start lg:justify-end">
              <div className="text-left lg:text-right max-w-md">
                <h2
                  className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-black leading-[1.05] mb-8"
                  style={{
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    letterSpacing: '-0.04em',
                    fontWeight: 300,
                  }}
                >
                  Donate your body to MASS BODY
                </h2>
              </div>
            </div>
            
            {/* Right Column - Content */}
            <div className="lg:col-span-7">
              <div className="space-y-6 max-w-2xl">
                <p
                  className="text-lg md:text-xl text-black/80 leading-[1.6]"
                  style={{
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    fontWeight: 400,
                  }}
                >
                  Begin the process of legally donating your body to an ecological art project.
                </p>
                <p
                  className="text-base md:text-lg text-black/60 leading-[1.6]"
                  style={{
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    fontWeight: 400,
                  }}
                >
                  This initial form collects basic information so a member of our team can contact you, answer questions, and guide you through the donation process.
                </p>
                <div className="pt-4">
                  <button
                    onClick={onClick}
                    className="px-10 py-3.5 border-2 border-black bg-black text-white text-xs uppercase tracking-[0.15em] transition-all duration-200 hover:bg-white hover:text-black"
                    style={{
                      fontFamily: 'Arial, Helvetica, sans-serif',
                    }}
                  >
                    Begin Body Donation Agreement
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Standard version - Pentagram asymmetric layout
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Left Column - Title */}
            <div className="lg:col-span-5 flex justify-start lg:justify-end">
              <div className="text-left lg:text-right max-w-md">
                <h2
                  className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-black leading-[1.05]"
                  style={{
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    letterSpacing: '-0.04em',
                    fontWeight: 300,
                  }}
                >
                  Donate Your<br />Body to Art
                </h2>
              </div>
            </div>
            
            {/* Right Column - Content */}
            <div className="lg:col-span-7">
              <div className="max-w-2xl space-y-6">
                <p
                  className="text-lg md:text-xl text-black/80 leading-[1.6]"
                  style={{
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    fontWeight: 400,
                  }}
                >
                  Ready to proceed?
                </p>
                <p
                  className="text-base md:text-lg text-black/60 leading-[1.6]"
                  style={{
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    fontWeight: 400,
                  }}
                >
                  Complete the Body Donation Agreement to formally begin the donation process.
                </p>
                <div className="pt-2">
                  <button
                    onClick={onClick}
                    className="px-10 py-3.5 border-2 border-black bg-black text-white text-xs uppercase tracking-[0.15em] transition-all duration-200 hover:bg-white hover:text-black"
                    style={{
                      fontFamily: 'Arial, Helvetica, sans-serif',
                    }}
                  >
                    Begin Body Donation Agreement
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Contact Section with Email Signup + Optional Message
const ContactSection = () => {
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [messageStatus, setMessageStatus] = useState('');

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setEmailStatus('sending');
    
    emailjs.init('PHHjdnlnzswyhX2_a');
    
    emailjs.send(
      'service_jh43zkf',
      'template_cab09n5',
      {
        name: 'Waitlist Signup',
        email: email,
        title: 'New Waitlist Signup',
        message: `New waitlist signup: ${email}`
      }
    )
    .then(() => {
      setEmailStatus('success');
      setEmail('');
      setTimeout(() => setEmailStatus(''), 3000);
    })
    .catch((error) => {
      console.error('EmailJS Error:', error);
      setEmailStatus('error');
      setTimeout(() => setEmailStatus(''), 3000);
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleMessageSubmit = (e) => {
    e.preventDefault();
    setMessageStatus('sending');
    
    emailjs.init('PHHjdnlnzswyhX2_a');
    
    emailjs.send(
      'service_jh43zkf',
      'template_cab09n5',
      {
        name: formData.name,
        email: formData.email,
        title: `Message from ${formData.name}`,
        message: formData.message
      }
    )
    .then(() => {
      setMessageStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => {
        setMessageStatus('');
        setShowMessage(false);
      }, 3000);
    })
    .catch((error) => {
      console.error('EmailJS Error:', error);
      setMessageStatus('error');
      setTimeout(() => setMessageStatus(''), 3000);
    });
  };

  return (
    <div className="w-full py-24 md:py-32 lg:py-40 border-t border-black/5">
      <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left Column - Title */}
          <div className="lg:col-span-5 flex justify-start lg:justify-end">
            <div className="text-left lg:text-right max-w-md">
              <h2
                className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-black leading-[1.05] mb-6"
                style={{
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  letterSpacing: '-0.04em',
                  fontWeight: 300,
                }}
              >
                Stay Connected
              </h2>
              <p
                className="text-xl md:text-2xl lg:text-3xl text-black/70 leading-[1.2]"
                style={{
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  fontWeight: 300,
                  letterSpacing: '-0.02em',
                }}
              >
                A new world awaits.
              </p>
            </div>
          </div>
          
          {/* Right Column - Form */}
          <div className="lg:col-span-7">
            <div className="max-w-2xl space-y-8">
              <p
                className="text-lg md:text-xl text-black/80 leading-[1.6]"
                style={{
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  fontWeight: 400,
                }}
              >
                Receive updates on the project's progress and site opening.
              </p>
              
              <form onSubmit={handleEmailSubmit} className="space-y-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    required
                    className="flex-1 bg-transparent border-b border-black/20 text-base md:text-lg text-black py-3 focus:border-black focus:outline-none transition-colors"
                    style={{ 
                      fontFamily: 'Arial, Helvetica, sans-serif',
                      fontWeight: 400
                    }}
                  />
                  <button
                    type="submit"
                    disabled={emailStatus === 'sending'}
                    className="px-10 py-3.5 border-2 border-black bg-black text-white text-xs uppercase tracking-[0.15em] transition-all duration-200 hover:bg-white hover:text-black disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-black disabled:hover:text-white"
                    style={{ 
                      fontFamily: 'Arial, Helvetica, sans-serif',
                    }}
                  >
                    {emailStatus === 'sending' ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
            
                {emailStatus === 'success' && (
                  <p className="text-black/60 text-sm" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                    Thank you for subscribing
                  </p>
                )}
                {emailStatus === 'error' && (
                  <p className="text-black/60 text-sm" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                    Failed to submit. Please try again.
                  </p>
                )}
              </form>

              {/* Add a Message Toggle */}
              {!showMessage ? (
                <div>
                  <button
                    onClick={() => setShowMessage(true)}
                    className="px-10 py-3.5 border border-black bg-transparent text-black text-xs uppercase tracking-[0.15em] transition-all duration-200 hover:bg-black hover:text-white"
                    style={{
                      fontFamily: 'Arial, Helvetica, sans-serif',
                    }}
                  >
                    Add a Message
                  </button>
                </div>
              ) : (
                // Message Form
                <div 
                  className="pt-10 mt-10 border-t border-black/5"
                  style={{
                    animation: 'fadeIn 0.3s ease-out'
                  }}
                >
                  <div className="flex items-center justify-between mb-8">
                    <h3
                      className="text-xl md:text-2xl lg:text-3xl text-black leading-[1.1]"
                      style={{
                        fontFamily: 'Arial, Helvetica, sans-serif',
                        fontWeight: 300,
                        letterSpacing: '-0.03em',
                      }}
                    >
                      Send a message
                    </h3>
                    <button
                      onClick={() => setShowMessage(false)}
                      className="text-black/40 hover:text-black text-2xl transition-colors"
                      style={{
                        fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                    >
                      ×
                    </button>
                  </div>
                  
                  <form onSubmit={handleMessageSubmit} className="space-y-8">
                    {/* Name Field */}
                    <div>
                      <label 
                        htmlFor="name"
                        className="block text-xs text-black/40 mb-3 uppercase tracking-[0.1em]"
                        style={{ 
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          fontWeight: 400,
                        }}
                      >
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-black/20 text-base md:text-lg text-black py-3 focus:border-black focus:outline-none transition-colors"
                        style={{ 
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          fontWeight: 400
                        }}
                      />
                    </div>
                          
                    {/* Email Field */}
                    <div>
                      <label 
                        htmlFor="contact-email"
                        className="block text-xs text-black/40 mb-3 uppercase tracking-[0.1em]"
                        style={{ 
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          fontWeight: 400,
                        }}
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-black/20 text-base md:text-lg text-black py-3 focus:border-black focus:outline-none transition-colors"
                        style={{ 
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          fontWeight: 400
                        }}
                      />
                    </div>

                    {/* Message Field */}
                    <div>
                      <label 
                        htmlFor="message"
                        className="block text-xs text-black/40 mb-3 uppercase tracking-[0.1em]"
                        style={{ 
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          fontWeight: 400,
                        }}
                      >
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="6"
                        className="w-full bg-transparent border-b border-black/20 text-base md:text-lg text-black py-3 focus:border-black focus:outline-none transition-colors resize-none"
                        style={{ 
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          fontWeight: 400
                        }}
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="pt-2 flex items-center gap-4">
                      <button
                        type="submit"
                        disabled={messageStatus === 'sending'}
                        className="px-10 py-3.5 border-2 border-black bg-black text-white text-xs uppercase tracking-[0.15em] transition-all duration-200 hover:bg-white hover:text-black disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-black disabled:hover:text-white"
                        style={{ 
                          fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                      >
                        {messageStatus === 'sending' ? 'Sending...' : 'Send Message'}
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => setShowMessage(false)}
                        className="px-6 py-3.5 text-black/40 hover:text-black transition-colors text-sm"
                        style={{ 
                          fontFamily: 'Arial, Helvetica, sans-serif',
                        }}
                      >
                        Cancel
                      </button>
                    </div>

                    {messageStatus === 'success' && (
                      <p className="text-black/60 text-sm" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                        Message sent successfully
                      </p>
                    )}
                    {messageStatus === 'error' && (
                      <p className="text-black/60 text-sm" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                        Failed to send message. Please try again.
                      </p>
                    )}
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FounderNarrative = () => {
  const navigate = useNavigate();

  const handleDonateClick = () => {
    navigate('/donate');
  };

  return (
    <article className="w-full bg-white">
      
      {/* Title Section - Pentagram Style */}
      <section className="w-full py-24 md:py-32 lg:py-40" aria-labelledby="main-heading">
        <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 
              id="main-heading"
              className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-black leading-[1.05]"
              style={{ 
                fontFamily: 'Arial, Helvetica, sans-serif', 
                letterSpacing: '-0.04em',
                fontWeight: 300,
              }}
            >
              MASS BODY is an ecological project dedicated to the artistic study of human decomposition.
            </h1>
          </div>
        </div>
      </section>

      {/* Philosophical Statement */}
      <PhilosophicalStatement />

      {/* Primary CTA - Prominent, Early Placement */}
      <PrimaryCTA onClick={handleDonateClick} isProminent={true} />

      {/* Press Section */}
      <PressSection />

      {/* About Section */}
      <AboutSection />

      {/* Secondary CTA - Reminder */}
      <PrimaryCTA onClick={handleDonateClick} />

      {/* Contact Section with Email + Optional Message (Tertiary) */}
      <ContactSection />

      {/* Add fadeIn animation */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

    </article>
  );
};

export default FounderNarrative;
